/**
 * LS Fence Styles — mobile slider controls.
 *
 * Desktop renders a plain grid, so everything here only has an effect while the
 * track is horizontally scrollable. The track itself is a native scroll-snap
 * carousel: swiping already works with JS off, this adds the dropdown jump,
 * the arrows, the dots and the counter.
 */
class LSFenceStyles {
  constructor(section) {
    this.section = section;
    this.track = section.querySelector('[data-lsfs-track]');
    if (!this.track) return;

    this.items = Array.from(section.querySelectorAll('[data-lsfs-item]'));
    if (this.items.length < 2) return;

    this.select = section.querySelector('[data-lsfs-select]');
    this.prev = section.querySelector('[data-lsfs-prev]');
    this.next = section.querySelector('[data-lsfs-next]');
    this.dots = Array.from(section.querySelectorAll('[data-lsfs-dot]'));
    this.progress = section.querySelector('[data-lsfs-dots]');
    this.current = section.querySelector('[data-lsfs-counter-current]');

    this.index = 0;
    this.onScroll = this.onScroll.bind(this);
    this.scrollTimer = null;

    this.bind();
    this.sync(0);
  }

  bind() {
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.select) {
      this.select.addEventListener('change', () => {
        this.goTo(parseInt(this.select.value, 10));
      });
    }

    if (this.prev) {
      this.prev.addEventListener('click', () => this.goTo(this.index - 1));
    }

    if (this.next) {
      this.next.addEventListener('click', () => this.goTo(this.index + 1));
    }

    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1);
      }
    });
  }

  /** True while the track is a carousel — false once CSS lays it out as a grid. */
  isSlider() {
    return this.track.scrollWidth > this.track.clientWidth + 1;
  }

  goTo(index) {
    const clamped = Math.max(0, Math.min(index, this.items.length - 1));
    const item = this.items[clamped];
    if (!item) return;

    if (this.isSlider()) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const left = item.offsetLeft - (this.track.clientWidth - item.clientWidth) / 2;
      this.track.scrollTo({
        left: Math.max(0, left),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    this.sync(clamped);
  }

  onScroll() {
    if (this.scrollTimer) window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let closest = 0;
      let smallest = Infinity;

      this.items.forEach((item, i) => {
        const distance = Math.abs(item.offsetLeft + item.clientWidth / 2 - center);
        if (distance < smallest) {
          smallest = distance;
          closest = i;
        }
      });

      this.sync(closest);
    }, 80);
  }

  sync(index) {
    this.index = index;

    if (this.select && parseInt(this.select.value, 10) !== index) {
      this.select.value = String(index);
    }

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('lsfs__dot--active', i === index);
    });

    if (this.progress) {
      this.progress.style.setProperty('--lsfs-progress', `${((index + 1) / this.items.length) * 100}%`);
    }

    if (this.current) {
      this.current.textContent = String(index + 1).padStart(2, '0');
    }

    if (this.prev) this.prev.disabled = index === 0;
    if (this.next) this.next.disabled = index === this.items.length - 1;
  }

  destroy() {
    this.track.removeEventListener('scroll', this.onScroll);
    if (this.scrollTimer) window.clearTimeout(this.scrollTimer);
  }
}

function initLSFenceStyles(scope) {
  (scope || document)
    .querySelectorAll('[data-section-type="ls-fence-styles"]')
    .forEach((section) => {
      if (section.dataset.lsfsReady === 'true') return;
      section.dataset.lsfsReady = 'true';
      new LSFenceStyles(section);
    });
}

initLSFenceStyles(document);
document.addEventListener('shopify:section:load', (event) => initLSFenceStyles(event.target));
